import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { NeonLogo } from "../components/common";
import { Button } from "../components/ui/button";

const C = { cyan: "#00FFD1", magenta: "#FF2EA6", green: "#39FF14", amber: "#FBBF24" };

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setStatus("failed"); return; }
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, []);

  const config = {
    checking: { icon: Loader2, spin: true, color: C.cyan, title: "Betaling controleren...", desc: "Een moment geduld, we bevestigen je betaling." },
    paid: { icon: CheckCircle2, color: C.green, title: "Gelukt! 🎉", desc: "Je abonnement is actief. Veel leesplezier!" },
    failed: { icon: XCircle, color: C.magenta, title: "Betaling niet voltooid", desc: "Er ging iets mis of de betaling is geannuleerd." },
    expired: { icon: XCircle, color: C.amber, title: "Sessie verlopen", desc: "Probeer het opnieuw via de prijzenpagina." },
  }[status];
  const Icon = config.icon;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center font-outfit text-white relative overflow-hidden p-6" style={{ background: "#050507" }}>
      <div className="absolute inset-0 neon-grid opacity-50 pointer-events-none" />
      <div className="glow-orb" style={{ width: 380, height: 380, background: C.cyan, top: -90, left: -70 }} />
      <div className="glow-orb" style={{ width: 320, height: 320, background: C.magenta, bottom: -60, right: -50 }} />

      <div className="relative z-10 mb-10"><NeonLogo /></div>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 rounded-3xl border-2 border-white/10 p-10 max-w-md w-full text-center" style={{ background: "#0A0A0F" }}>
        <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6" style={{ background: `${config.color}1A`, boxShadow: `0 0 24px ${config.color}55` }}>
          <Icon className={`w-10 h-10 ${config.spin ? "animate-spin" : ""}`} strokeWidth={2.5} style={{ color: config.color }} />
        </div>
        <h1 className="font-display text-3xl font-extrabold" data-testid="payment-status-title">{config.title}</h1>
        <p className="mt-3 text-slate-400 font-medium">{config.desc}</p>
        {status !== "checking" && (
          <div className="mt-8 flex flex-col gap-2">
            <Button onClick={() => navigate("/")} className="h-12 rounded-xl font-bold text-black box-glow-cyan border-0 hover:opacity-90 active:scale-95" style={{ background: C.cyan }} data-testid="payment-home-btn">Naar home</Button>
            {status !== "paid" && <Button onClick={() => navigate("/prijzen")} className="h-12 rounded-xl font-bold bg-transparent border-2 border-white/15 text-white hover:bg-white/5" data-testid="payment-retry-btn">Opnieuw proberen</Button>}
          </div>
        )}
      </motion.div>
    </div>
  );
}
